import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { configApi } from '@/api/config-api'
import type { Config } from '@/types/config'
import { APISession, isValidAPISession } from '@/types/session'

const DEFAULT_CONFIG: Config = {
  PK: 'CONFIG',
  SK: 'SETTINGS',
  ActiveSession: '2024RS' as APISession,
  EnableSyncJob: false,
  Prompts: {
    LegislativeReview: null,
    Refinement: null,
    Opposition: null,
    Support: null,
    ShouldMonitor: null
  }
}

export const useConfig = () => {
  const queryClient = useQueryClient()

  const { data, isPending, isError } = useQuery({
    queryKey: ['config'],
    queryFn: async () => {
      try {
        const config = await configApi.getConfig()
        console.log('🔧 Raw config:', config)
        
        // Validate API session format
        if (!config.ActiveSession || !isValidAPISession(config.ActiveSession)) {
          console.warn(`Invalid session format in config: ${config.ActiveSession}, defaulting to 2024RS`)
          config.ActiveSession = DEFAULT_CONFIG.ActiveSession
        }
        
        return config
      } catch (error) {
        console.error('Failed to load config:', error)
        throw error
      }
    }
  })

  const mutation = useMutation({
    mutationFn: async (data: Partial<Config>) => {
      console.log('Updating config with:', data)
      return configApi.updateConfig(data)
    },
    onSuccess: () => {
      // Invalidate both config and bills queries
      queryClient.invalidateQueries({ queryKey: ['config'] })
      queryClient.invalidateQueries({ queryKey: ['bills'] })
    },
    onError: (error) => {
      console.error('Failed to update config:', error)
      throw error
    }
  })

  return {
    config: data || DEFAULT_CONFIG,
    isLoading: isPending,
    error: isError ? 'Failed to load configuration. Using default settings.' : null,
    updateConfig: mutation
  }
}